import type { ExternalTableSchema } from '@packages/table-builder';

export const warrantsTable: ExternalTableSchema = {
  metadata: {
    title: 'Warrants',
    entity: 'Warrants',
  },

  dataSource: {
    endpoint: '/api/cap-table/api/app/warrant/warrant-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      MaxResultCount: 'table.pageSize',
      SkipCount: 'table.pageOffset',
      Sorting: 'table.sorting',
    },

    filters: [
      {
        name: 'StockClassId',
        placeholder: 'Stock Class',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/cap-table/api/app/stock-class/current-issuer-stock-class-list',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'AwardStatus',
        placeholder: 'Status',
        type: 'select',
        multiple: true,
        options: [
          { label: 'Outstanding', value: 'Outstanding' },
          { label: 'Partially Vested', value: 'Partially Vested' },
          { label: 'Fully Vested', value: 'Fully Vested' },
          { label: 'Expired', value: 'Expired' },
          { label: 'Cancelled', value: 'Cancelled' },
        ],
      },
      {
        name: 'PublishStatus',
        placeholder: 'Publish Status',
        type: 'select',
        multiple: true,
        options: [
          { label: 'Pending', value: 'Pending' },
          { label: 'Published', value: 'Published' },
        ],
      },
      {
        name: 'IssueFromDate',
        placeholder: 'Issue Date From',
        type: 'date',
      },
      {
        name: 'IssueToDate',
        placeholder: 'Issue Date To',
        type: 'date',
      },
      {
        name: 'Acceptance',
        placeholder: 'Accept Status',
        type: 'select',
        options: [
          { label: 'Accepted', value: 1 },
          { label: 'Needs Acceptance', value: 2 },
        ],
      },
    ],
  },

  columns: [
    {
      field: 'participantName',
      header: 'Holder',
      type: 'couple',
      sortable: true,
      couple: {
        titleField: 'participantName',
        subtitleField: 'employeeId',
        showTag: true,
      },
      width: 200,
      visibility: 'locked',
    },
    {
      field: 'warrantName',
      header: 'Warrant Name',
      sortable: true,
      width: 180,
      emptyDisplay: '',
    },
    {
      field: 'warrantTypeName',
      header: 'Warrant Type',
      type: 'badge',
      sortable: true,
      width: 150,
      align: 'center',
      badge: {
        statusMap: {
          Warrant: 'info',
          'Pre-Funded Warrant': 'info',
        },
        defaultVariant: 'info',
      },
    },
    {
      field: 'awardStatusName',
      header: 'Status',
      type: 'badge',
      sortable: true,
      width: 150,
      align: 'center',
      uppercase: true,
      badge: {
        statusMap: {
          Outstanding: 'info',
          'Partially Vested': 'info',
          'Fully Vested': 'success',
          Expired: 'warning',
          Cancelled: 'error',
        },
        defaultVariant: 'default',
      },
    },
    {
      field: 'publishStatusName',
      header: 'Publish Status',
      type: 'badge',
      sortable: true,
      width: 140,
      align: 'center',
      uppercase: true,
      badge: {
        statusMap: {
          Pending: 'default',
          Published: 'success',
        },
        defaultVariant: 'default',
      },
    },
    {
      field: 'instrumentNumber',
      header: 'Instrument Number',
      sortable: true,
      width: 160,
    },
    {
      field: 'serviceTypeName',
      header: 'Service Type',
      sortable: true,
      width: 150,
    },
    {
      field: 'quantity',
      header: 'Issued',
      type: 'number',
      sortable: true,
      width: 100,
      align: 'right',
    },
    {
      field: 'vestedQuantity',
      header: 'Vested',
      type: 'number',
      sortable: true,
      width: 100,
      align: 'right',
    },
    {
      field: 'unvestedQuantity',
      header: 'Unvested',
      type: 'number',
      sortable: true,
      width: 100,
      align: 'right',
    },
    {
      field: 'exercisedQuantity',
      header: 'Exercised',
      type: 'number',
      sortable: true,
      width: 100,
      align: 'right',
    },
    {
      field: 'cancelledQuantity',
      header: 'Cancelled',
      type: 'number',
      sortable: true,
      width: 100,
      align: 'right',
    },
    {
      field: 'outstandingQuantity',
      header: 'Outstanding',
      type: 'number',
      sortable: true,
      width: 120,
      align: 'right',
    },
    {
      field: 'issueDate',
      header: 'Issue Date',
      type: 'date',
      sortable: true,
      width: 120,
    },
    {
      field: 'expirationDate',
      header: 'Expiration Date',
      type: 'date',
      sortable: true,
      width: 120,
    },

    // ── Column-picker only ──────────────────────────────────────────────
    {
      field: 'exercisableQuantity',
      header: 'Exercisable',
      type: 'number',
      sortable: true,
      width: 110,
      align: 'right',
      visibility: 'picker',
    },
    {
      field: 'remainingQuantity',
      header: 'Remaining',
      type: 'number',
      sortable: true,
      width: 100,
      align: 'right',
      visibility: 'picker',
    },
    {
      field: 'cashlessAllowed',
      header: 'Cashless',
      type: 'badge',
      sortable: true,
      width: 120,
      align: 'center',
      visibility: 'picker',
      conditionalValues: { true: 'Yes', false: 'No' },
      badge: {
        statusMap: { Yes: 'success', No: 'error' },
      },
    },
    {
      field: 'exercisePriceAmount',
      header: 'Strike Price',
      type: 'currency',
      sortable: true,
      width: 120,
      align: 'right',
      visibility: 'picker',
    },
    {
      field: 'vestingTemplateName',
      header: 'Vesting Template',
      sortable: true,
      width: 160,
      visibility: 'picker',
    },
  ],

  actions: {
    row: [
      { id: 'VIEW_WARRANTS', label: 'View' },
      { id: 'EDIT_WARRANTS', label: 'Edit' },
      // Hidden for now — page/route kept at /cap-table/warrants/milestones
      // { id: 'MILESTONES_WARRANTS', label: 'Milestones' },
      { id: 'PUBLISH_WARRANTS', label: 'Publish' },
      { id: 'CANCEL_WARRANTS', label: 'Cancel', variant: 'danger' },
      { id: 'DELETE_WARRANTS', label: 'Delete', variant: 'danger' },
    ],
    bulk: [
      { id: 'BULK_DELETE_WARRANTS', label: 'Bulk Delete', variant: 'danger' },
    ],
    toolbar: {
      primary: {
        id: 'ADD_WARRANTS',
        label: 'Issue Warrant',
        icon: 'add_circle_outline',
      },
      secondary: [
        {
          id: 'EXERCISE_WARRANTS',
          label: 'Exercise Warrants',
          icon: 'bolt',
        },
        {
          label: 'Downloads',
          icon: 'download',
          type: 'download',
          children: [
            { id: 'DOWNLOAD_TEMPLATE_WARRANTS', label: 'Download Template' },
          ],
        },
        {
          label: 'Import',
          icon: 'upload',
          type: 'upload',
          children: [
            { id: 'IMPORT_WARRANTS', label: 'Import Bulk XLS' },
          ],
        },
      ],
    },
  },

  features: {
    search: { enabled: true },
    selection: { keyField: 'id', selectAllPages: true },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    columnPreferences: { persistenceKey: 'Warrants Admin', resizable: true, reorderable: true },
  },
};
